import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { BatchProgress, TicketProgress } from "../types/operation";

const STATUS_ORDER: Record<string, number> = {
  running: 0,
  pending: 1,
  success: 2,
  skipped: 3,
  failed: 4,
};

const STATUS_ICON: Record<string, string> = {
  running: "▶",
  pending: "•",
  success: "✓",
  skipped: "⊘",
  failed: "✗",
};

const STATUS_COLOR: Record<string, string> = {
  running: "info.main",
  pending: "text.secondary",
  success: "success.main",
  skipped: "text.secondary",
  failed: "error.main",
};

// Motivos estáveis do ticket no lote (OpenAPI do BFF, TicketProgress.reason).
// Motivo desconhecido aparece cru.
const REASON_LABEL: Record<string, string> = {
  cleanup_in_progress: "Limpeza em andamento, tente em alguns minutos",
  hour_in_use: "Outra reidratação em andamento, tente em alguns minutos",
  state_error: "Erro ao gravar o estado da reidratação",
  blob_download_error: "Falha ao baixar o backup",
  blob_scan_error: "Falha ao ler o backup",
  insert_error: "Falha ao gravar os eventos",
  rehydration_in_progress: "Reidratação em andamento",
  already_rehydrated: "Já reidratado",
  no_backup_found: "Sem backup",
};

function shortId(id: string): string {
  return id.length <= 8 ? id : id.slice(0, 8);
}

function compareTickets(a: TicketProgress, b: TicketProgress): number {
  const orderA = STATUS_ORDER[a.status] ?? 99;
  const orderB = STATUS_ORDER[b.status] ?? 99;
  if (orderA !== orderB) return orderA - orderB;
  return a.ticketId.localeCompare(b.ticketId);
}

// summarizeTickets: "2 ok · 1 falhou · 1 pulado" (contagens zero ficam fora);
// null quando nenhum ticket terminou ainda.
function summarizeTickets(tickets: TicketProgress[]): string | null {
  const count = (status: string) => tickets.filter((t) => t.status === status).length;
  const ok = count("success");
  const failed = count("failed");
  const skipped = count("skipped");
  const parts = [
    ok > 0 ? `${ok} ok` : null,
    failed > 0 ? `${failed} ${failed === 1 ? "falhou" : "falharam"}` : null,
    skipped > 0 ? `${skipped} ${skipped === 1 ? "pulado" : "pulados"}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function BatchSummary({ tickets }: { tickets: TicketProgress[] }) {
  const summary = summarizeTickets(tickets);
  return (
    <Typography variant="body2" color="text.secondary" aria-label="Resumo dos tickets">
      {summary ?? ""}
    </Typography>
  );
}

type BatchDetailsProps = {
  open: boolean;
  batch: BatchProgress | undefined;
};

export function BatchDetails({ open, batch }: BatchDetailsProps) {
  if (!batch) {
    return (
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Sem dados de tickets ainda.
          </Typography>
        </Box>
      </Collapse>
    );
  }

  const sorted = [...batch.tickets].sort(compareTickets);

  return (
    <Collapse in={open} unmountOnExit>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {sorted.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum ticket registrado.
          </Typography>
        ) : (
          sorted.map((ticket) => {
            const icon = STATUS_ICON[ticket.status] ?? "○";
            const color = STATUS_COLOR[ticket.status] ?? "text.secondary";
            const showProgress =
              ticket.status === "running" && ticket.totalExpected > 0;
            const pct =
              showProgress && ticket.totalExpected > 0
                ? Math.min(
                    100,
                    Math.round(
                      (ticket.eventsInserted / ticket.totalExpected) * 100,
                    ),
                  )
                : 0;

            return (
              <Box
                key={ticket.ticketId}
                sx={{
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  px: 1.5,
                  py: 1,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      component="span"
                      sx={{ color, fontWeight: 700, minWidth: 16 }}
                      aria-hidden="true"
                    >
                      {icon}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {shortId(ticket.ticketId)}
                    </Typography>
                    <Typography variant="body2" sx={{ color }}>
                      {ticket.status}
                      {ticket.reason
                        ? ` (${REASON_LABEL[ticket.reason] ?? ticket.reason})`
                        : ""}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {ticket.totalExpected > 0
                      ? `${ticket.eventsInserted}/${ticket.totalExpected} eventos`
                      : `${ticket.eventsInserted} eventos`}
                  </Typography>
                </Stack>
                {showProgress ? (
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{ mt: 0.5, height: 4, borderRadius: 1 }}
                    aria-label={`Progresso do ticket ${shortId(ticket.ticketId)}`}
                  />
                ) : null}
              </Box>
            );
          })
        )}
      </Stack>
    </Collapse>
  );
}
